/**
 * Peperoni — Daemonenjaeger (Pepperion, Backline-Springer). LMAO-Style (BATTLE-17):
 * "lauernder Fel-Assassine" — schlanke, dynamisch gebogene Chili-Schote in glaenzendem
 * Chili-Rot (Hochglanz-Haut: langer Gloss-Streak + kraeftige Speculars), unten spitz
 * in einem Klingen-Flick auslaufend; oben ein gruener Kelch mit Sepal-Zacken, dessen
 * Stiel sich in ZWEI abstehende HOERNER spaltet (Daemonenjaeger-Motiv).
 * 2★: Leder-Bandolier mit Nieten + Metall-Hornringe.
 * 3★: gruenes Fel-Glimmen an den Kelch-Hoernern (Kanten-Glint + Flammen-Wisps,
 *     pulsiert mit pose.castPulse). Fel-Gruen wird aus colors.accent abgeleitet —
 *     dead-State ist damit automatisch entsaettigt (Glows werden eh geskippt).
 * Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'

export const paintPeperoni: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const outlineW = Math.max(2, r * 0.12)
  // Fel-Gruen aus dem Kelch-Accent abgeleitet (kein Fremd-Hex noetig)
  const fel = lighten(colors.accent, 42)
  const felCore = lighten(colors.accent, 62)
  const metal = lighten(colors.outline, 52)

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'
  ctx.translate(x, y)
  ctx.rotate(0.06) // leichter Lean — der Jaeger steht nie ganz aufrecht

  // --- Stiel-HOERNER (unter dem Kelch gezeichnet, ragen als Sicheln heraus):
  //     breite Basis, kraeftige Krummung nach aussen-oben = Daemonen-Silhouette ---
  const hornTips: Array<[number, number]> = []
  const drawHorn = (hx: number, hy: number, ang: number, len: number, flip: 1 | -1): void => {
    const w = len * 0.46
    // Tip-Position im Body-Frame merken (fuer Fel-Wisps nach dem Kelch)
    const tlx = flip * len * 0.72
    const tly = -len * 0.82
    hornTips.push([
      hx + tlx * Math.cos(ang) - tly * Math.sin(ang),
      hy + tlx * Math.sin(ang) + tly * Math.cos(ang),
    ])
    ctx.save()
    ctx.translate(hx, hy)
    ctx.rotate(ang)
    ctx.scale(flip, 1)
    const hornBody = (): void => {
      ctx.beginPath()
      ctx.moveTo(-w * 0.55, 0)
      // Aussenkante: buchtet weit nach aussen, laeuft spitz in den Tip
      ctx.bezierCurveTo(-w * 0.85, -len * 0.52, -len * 0.02, -len * 0.92, len * 0.72, -len * 0.82)
      // Innenkante (konkav) zurueck zur breiten Basis
      ctx.bezierCurveTo(len * 0.26, -len * 0.5, w * 0.5, -len * 0.26, w * 0.55, 0)
      ctx.closePath()
    }
    const g = ctx.createLinearGradient(0, 0, len * 0.5, -len * 0.9)
    g.addColorStop(0, darken(colors.accent, 18))
    g.addColorStop(0.55, colors.accent)
    g.addColorStop(1, lighten(colors.accent, 16))
    ctx.fillStyle = g
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.08)
    hornBody()
    ctx.fill()
    ctx.stroke()
    // Licht-Kante entlang der Aussenkante (Lichtquelle oben-links)
    if (!dead) {
      ctx.strokeStyle = rgba(lighten(colors.accent, 34), 0.8)
      ctx.lineWidth = Math.max(1, r * 0.04)
      ctx.beginPath()
      ctx.moveTo(-w * 0.45, -len * 0.14)
      ctx.bezierCurveTo(-w * 0.68, -len * 0.5, -len * 0.02, -len * 0.82, len * 0.58, -len * 0.78)
      ctx.stroke()
    }
    // 2★: Metall-Hornring (Armor-Eskalation, docs/13 §4)
    if (tier >= 2 && !dead) {
      const mg = ctx.createLinearGradient(0, -len * 0.6, 0, -len * 0.32)
      mg.addColorStop(0, metal)
      mg.addColorStop(1, darken(metal, 35))
      ctx.fillStyle = mg
      ctx.strokeStyle = colors.outline
      ctx.lineWidth = Math.max(1, r * 0.03)
      ctx.beginPath()
      ctx.ellipse(len * 0.16, -len * 0.5, w * 0.4, w * 0.19, -0.75, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }
    // 3★: Fel-Glint entlang der Hornkante (glimmt, pulsiert beim Cast)
    if (tier >= 3 && !dead) {
      ctx.save()
      ctx.strokeStyle = rgba(fel, 0.55 + pose.castPulse * 0.35)
      ctx.lineWidth = Math.max(1, r * 0.035)
      ctx.shadowColor = fel
      ctx.shadowBlur = r * (0.12 + pose.castPulse * 0.1)
      ctx.beginPath()
      ctx.moveTo(len * 0.1, -len * 0.62)
      ctx.bezierCurveTo(len * 0.3, -len * 0.8, len * 0.5, -len * 0.84, len * 0.68, -len * 0.8)
      ctx.stroke()
      ctx.restore()
    }
    ctx.restore()
  }
  drawHorn(-r * 0.26, -r * 0.9, 0.2, r * 0.54, -1)
  drawHorn(r * 0.28, -r * 0.88, -0.14, r * 0.5, 1)

  // --- Korpus: schlanke, gebogene Chili-Schote — Bauch rechts, Innenkurve links,
  //     unten spitz auslaufender Klingen-Flick nach links (lauernde Assassinen-Kurve) ---
  const podPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(-r * 0.52, -r * 0.5)
    ctx.bezierCurveTo(-r * 0.58, -r * 0.86, -r * 0.26, -r * 1.0, r * 0.04, -r * 0.98)
    ctx.bezierCurveTo(r * 0.36, -r * 0.96, r * 0.56, -r * 0.7, r * 0.6, -r * 0.4)
    ctx.bezierCurveTo(r * 0.66, 0, r * 0.6, r * 0.3, r * 0.44, r * 0.58)
    // Unterseite konvergiert in die Spitze (scharfer Cusp = Chili-Tip)
    ctx.bezierCurveTo(r * 0.3, r * 0.84, r * 0.05, r * 0.98, -r * 0.44, r * 1.04)
    // Innenkurve zurueck nach oben (leicht konkav = Biegung der Schote)
    ctx.bezierCurveTo(-r * 0.36, r * 0.8, -r * 0.47, r * 0.48, -r * 0.54, r * 0.2)
    ctx.bezierCurveTo(-r * 0.6, -r * 0.08, -r * 0.58, -r * 0.28, -r * 0.52, -r * 0.5)
    ctx.closePath()
  }
  podPath()
  ctx.fillStyle = volumeGradient(ctx, -r * 0.1, -r * 0.12, r, colors.body, 24, 26)
  ctx.fill()
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  ctx.stroke()

  // --- Innenleben, geclippt auf die Schote ---
  ctx.save()
  podPath()
  ctx.clip()
  // Schattenflanke rechts-unten (bodyDark) — verkauft die Rundung der Schote
  ctx.strokeStyle = rgba(darken(colors.bodyDark, 8), 0.5)
  ctx.lineWidth = r * 0.2
  ctx.beginPath()
  ctx.moveTo(r * 0.52, -r * 0.42)
  ctx.quadraticCurveTo(r * 0.68, r * 0.12, r * 0.26, r * 0.78)
  ctx.stroke()
  // Laengs-Falz folgt der Biegung (Chili-Charakteristik, dezent — Gesicht bleibt ruhig)
  ctx.strokeStyle = rgba(darken(colors.body, 24), 0.4)
  ctx.lineWidth = Math.max(1, r * 0.05)
  ctx.beginPath()
  ctx.moveTo(r * 0.32, -r * 0.72)
  ctx.quadraticCurveTo(r * 0.46, 0, r * 0.02, r * 0.86)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(-r * 0.4, -r * 0.62)
  ctx.quadraticCurveTo(-r * 0.48, -r * 0.32, -r * 0.5, -r * 0.02)
  ctx.stroke()
  // Spitze laeuft dunkler aus (reife Chili)
  ctx.strokeStyle = rgba(darken(colors.body, 30), 0.5)
  ctx.lineWidth = r * 0.14
  ctx.beginPath()
  ctx.moveTo(-r * 0.02, r * 0.97)
  ctx.quadraticCurveTo(-r * 0.24, r * 1.04, -r * 0.42, r * 1.02)
  ctx.stroke()

  // --- Hochglanz-Haut (Signature): langer Gloss-Streak + Sekundaer-Reflexe ---
  if (!dead) {
    ctx.strokeStyle = rgba(lighten(colors.body, 52), 0.7)
    ctx.lineWidth = r * 0.09
    ctx.beginPath()
    ctx.moveTo(-r * 0.36, -r * 0.56)
    ctx.quadraticCurveTo(-r * 0.5, -r * 0.02, -r * 0.32, r * 0.48)
    ctx.stroke()
    // kurzer Parallel-Reflex Richtung Spitze (nasser Lack-Look)
    ctx.strokeStyle = rgba(lighten(colors.body, 44), 0.5)
    ctx.lineWidth = r * 0.05
    ctx.beginPath()
    ctx.moveTo(-r * 0.24, r * 0.62)
    ctx.quadraticCurveTo(-r * 0.34, r * 0.8, -r * 0.38, r * 0.92)
    ctx.stroke()
    // Glanz auf der rechten Schulter (glatte Haut faengt auch oben Licht)
    ctx.strokeStyle = rgba(lighten(colors.body, 40), 0.35)
    ctx.lineWidth = r * 0.06
    ctx.beginPath()
    ctx.moveTo(r * 0.28, -r * 0.78)
    ctx.quadraticCurveTo(r * 0.46, -r * 0.66, r * 0.52, -r * 0.44)
    ctx.stroke()
  }

  // --- 2★: Leder-Bandolier quer ueber die Brust (unterhalb der Gesichts-Zone) ---
  if (tier >= 2 && !dead) {
    const leather = darken(colors.bodyDark, 18)
    const strap = (oy: number): void => {
      ctx.beginPath()
      ctx.moveTo(-r * 0.62, r * (0.3 + oy))
      ctx.quadraticCurveTo(-r * 0.05, r * (0.52 + oy), r * 0.55, r * (0.6 + oy))
    }
    ctx.strokeStyle = darken(leather, 14)
    ctx.lineWidth = r * 0.2
    strap(0)
    ctx.stroke()
    ctx.strokeStyle = leather
    ctx.lineWidth = r * 0.15
    strap(0)
    ctx.stroke()
    // Naht-Highlight an der Oberkante
    ctx.strokeStyle = rgba(lighten(leather, 30), 0.8)
    ctx.lineWidth = Math.max(1, r * 0.03)
    strap(-0.065)
    ctx.stroke()
    // Nieten
    ctx.fillStyle = metal
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1, r * 0.025)
    for (const [nx, ny] of [[-0.4, 0.39], [0.34, 0.575]] as const) {
      ctx.beginPath()
      ctx.arc(r * nx, r * ny, r * 0.04, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }
    // Raute-Schnalle (Assassinen-Emblem) mittig auf dem Gurt
    ctx.save()
    ctx.translate(-r * 0.04, r * 0.5)
    ctx.rotate(Math.PI / 4)
    const s = r * 0.09
    ctx.fillStyle = metal
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1, r * 0.04)
    ctx.fillRect(-s, -s, s * 2, s * 2)
    ctx.strokeRect(-s, -s, s * 2, s * 2)
    ctx.fillStyle = darken(metal, 32)
    ctx.fillRect(-s * 0.35, -s * 0.35, s * 0.7, s * 0.7)
    ctx.restore()
  }
  ctx.restore() // Clip Ende

  // Outline nach dem Innenleben knackig nachziehen (Silhouetten-Readability)
  podPath()
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  ctx.stroke()

  // --- Kelch: gruene Kappe mit haengenden Sepal-Zacken (Mitte kurz — Stirn frei) ---
  const calyxPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(-r * 0.6, -r * 0.52)
    ctx.quadraticCurveTo(-r * 0.56, -r * 0.9, -r * 0.2, -r * 1.02)
    ctx.quadraticCurveTo(r * 0.06, -r * 1.1, r * 0.34, -r * 0.98)
    ctx.quadraticCurveTo(r * 0.58, -r * 0.86, r * 0.62, -r * 0.52)
    // Sepal-Zickzack von rechts nach links (Zacken haengen auf die Schote)
    ctx.lineTo(r * 0.47, -r * 0.36)
    ctx.lineTo(r * 0.33, -r * 0.6)
    ctx.lineTo(r * 0.15, -r * 0.42)
    ctx.lineTo(r * 0.01, -r * 0.56)
    ctx.lineTo(-r * 0.15, -r * 0.42)
    ctx.lineTo(-r * 0.31, -r * 0.6)
    ctx.lineTo(-r * 0.46, -r * 0.38)
    ctx.closePath()
  }
  const cg = ctx.createLinearGradient(-r * 0.5, -r * 1.06, r * 0.34, -r * 0.4)
  cg.addColorStop(0, lighten(colors.accent, 26))
  cg.addColorStop(0.55, colors.accent)
  cg.addColorStop(1, darken(colors.accent, 22))
  calyxPath()
  ctx.fillStyle = cg
  ctx.fill()
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1.5, r * 0.08)
  ctx.stroke()
  // Sepal-Mittelrippen (organische Tiefe)
  ctx.save()
  ctx.strokeStyle = rgba(darken(colors.accent, 26), 0.55)
  ctx.lineWidth = Math.max(1, r * 0.035)
  for (const [sx, sy] of [[0.4, -0.42], [0.08, -0.48], [-0.23, -0.48]] as const) {
    ctx.beginPath()
    ctx.moveTo(r * sx * 0.75, -r * 0.78)
    ctx.quadraticCurveTo(r * sx * 0.9, -r * 0.62, r * sx, r * sy)
    ctx.stroke()
  }
  ctx.restore()
  // Licht-Kante am Kelch-Dom (oben-links) + kleiner Glanz
  if (!dead) {
    ctx.strokeStyle = rgba(lighten(colors.accent, 46), 0.7)
    ctx.lineWidth = Math.max(1, r * 0.04)
    ctx.beginPath()
    ctx.moveTo(-r * 0.52, -r * 0.68)
    ctx.quadraticCurveTo(-r * 0.42, -r * 0.94, -r * 0.08, -r * 1.0)
    ctx.stroke()
    specular(ctx, -r * 0.26, -r * 0.86, r * 0.09, 0.35)
  }

  // --- 3★: Fel-Glimmen + Flammen-Wisps an den Horn-Spitzen (docs/13 §5: Glow off-body) ---
  if (tier >= 3 && !dead) {
    for (const [tx, ty] of hornTips) {
      const lean = tx < 0 ? -1 : 1
      glow(ctx, tx, ty, r * (0.26 + pose.castPulse * 0.16), fel, 0.4 + pose.castPulse * 0.35)
      ctx.save()
      ctx.shadowColor = fel
      ctx.shadowBlur = r * (0.16 + pose.castPulse * 0.12)
      const h = r * (0.22 + pose.castPulse * 0.12)
      const wisp = (scale: number, col: string): void => {
        ctx.fillStyle = col
        ctx.beginPath()
        ctx.moveTo(tx + lean * h * 0.28 * scale, ty - h * scale)
        ctx.quadraticCurveTo(tx + h * 0.42 * scale, ty - h * 0.3 * scale, tx, ty + h * 0.18 * scale)
        ctx.quadraticCurveTo(tx - h * 0.42 * scale, ty - h * 0.3 * scale, tx + lean * h * 0.28 * scale, ty - h * scale)
        ctx.closePath()
        ctx.fill()
      }
      wisp(1, rgba(fel, 0.85))
      ctx.shadowBlur = 0
      wisp(0.55, rgba(felCore, 0.9))
      ctx.restore()
    }
  }

  // --- Licht: Rim oben-links + kraeftige Speculars (Hochglanz!) — nie auf Toten ---
  if (!dead) {
    rimLight(ctx, 0, r * 0.02, r * 0.64, r * 0.96, rgba(lighten(colors.body, 55), 0.42), Math.max(1.5, r * 0.06))
    specular(ctx, -r * 0.3, -r * 0.56, r * 0.17, 0.7)
    specular(ctx, -r * 0.42, r * 0.3, r * 0.09, 0.45)
    specular(ctx, -r * 0.36, r * 0.86, r * 0.06, 0.4)
  }

  ctx.restore()
}
